import React, { useState } from 'react';
import { Plus, MessageSquare, AlertCircle } from 'lucide-react';
import { Button, Badge, Card, CardHeader, CardTitle, CardContent, Calendar } from '@clinic-os/ui';
import { useAppointmentsByDate, useCreateAppointment } from '../../hooks/useAppointments';
import { useSendAppointmentReminder, useMessagingConfigured } from '../../hooks/useMessaging';
import { BookAppointmentDialog } from './BookAppointmentDialog';
import type { Appointment } from '@clinic-os/shared/types';

const statusVariant: Record<string, 'success' | 'warning' | 'error' | 'default' | 'outline'> = {
  confirmed: 'success',
  scheduled: 'default',
  in_progress: 'warning',
  completed: 'outline',
  cancelled: 'error',
  no_show: 'error',
};

export function AppointmentsPage() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showBook, setShowBook] = useState(false);
  const { data: appointments = [], isLoading } = useAppointmentsByDate(selectedDate);
  const createAppointment = useCreateAppointment();
  const sendReminder = useSendAppointmentReminder();
  const isMessagingConfigured = useMessagingConfigured();

  const sortedAppointments = [...appointments].sort((a, b) =>
    new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
  );

  const handleBook = (data: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>) => {
    createAppointment.mutate(data);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Appointments</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your schedule and patient appointments</p>
        </div>
        <Button size="lg" onClick={() => setShowBook(true)}>
          <Plus className="w-4 h-4" />
          New Appointment
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Select Date</CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar selected={selectedDate} onSelect={setSelectedDate} />
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>
              {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="py-12 text-center text-slate-500">Loading...</div>
            ) : sortedAppointments.length === 0 ? (
              <div className="py-12 text-center text-slate-500">
                <p className="text-sm">No appointments on this day</p>
                <Button variant="outline" className="mt-4" size="sm" onClick={() => setShowBook(true)}>
                  <Plus className="w-4 h-4" />
                  Add appointment
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {sortedAppointments.map((apt) => (
                  <div
                    key={apt.id}
                    className="flex items-center gap-4 p-4 rounded-lg border border-slate-200 hover:border-blue-200 hover:bg-blue-50/30 transition-colors cursor-pointer"
                  >
                    <div className="flex-shrink-0 text-sm font-medium text-slate-500 w-16">
                      {new Date(apt.scheduledAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900">{apt.type}</p>
                      <p className="text-sm text-slate-500">
                        {apt.patientId ? `Patient #${apt.patientId}` : 'No patient'} · {apt.durationMinutes} min
                      </p>
                      {apt.notes && (
                        <p className="text-xs text-slate-500 mt-1 truncate">{apt.notes}</p>
                      )}
                    </div>
                    <Badge variant={statusVariant[apt.status]}>{apt.status.replace('_', ' ')}</Badge>
                    {apt.status === 'scheduled' || apt.status === 'confirmed' ? (
                      isMessagingConfigured ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            sendReminder.mutate({ appointment: apt, channel: 'whatsapp' });
                          }}
                          disabled={sendReminder.isPending}
                          title="Send reminder via WhatsApp"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </Button>
                      ) : (
                        <div className="group relative">
                          <Button variant="ghost" size="sm" disabled title="Messaging not configured">
                            <MessageSquare className="w-4 h-4" />
                          </Button>
                          <div className="absolute bottom-full right-0 mb-1 px-2 py-1 bg-slate-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-10">
                            Not configured
                          </div>
                        </div>
                      )
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <BookAppointmentDialog
        open={showBook}
        onClose={() => setShowBook(false)}
        onBook={handleBook}
        defaultDate={selectedDate}
      />
    </div>
  );
}
